/**
 * MirrorSkillEffect — Conjuration Tether path evolution: autonomous shade.
 *
 * Evolution that replaces Tether. The shade is no longer tethered to mirroring
 * player movement — it moves freely, selecting its own targets via the
 * 'autonomous' AI mode. Shade damage rises to 75% of player base damage
 * (up from Tether's 40%, surpassing Clarity's 55% via max aggregation).
 * The HP pool remains shared: shade damage still routes to the player.
 *
 * Because Mirror has `replacesId: 'tether'`, all Tether upgrades (Clarity,
 * Synapse) continue to apply. The resolver scope used for key lookups is
 * 'tether' — not 'mirror' — so inherited upgrade effects propagate naturally.
 *
 * Toggle on → shade spawns at player position, begins autonomous targeting.
 * Toggle off → shade dismissed, cooldown starts.
 *
 * Medium cooldown tier (1200ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Mirror
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { ShadeSummonConfig } from './TetherSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface MirrorDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getBaseDamage(): number;
  };
  readonly targeting: {
    /** Returns true if the shade should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: ShadeSummonConfig, x: number, y: number): string;
    destroy(instanceId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface MirrorSkillConfig {
  readonly cooldownMs: number;
  /** Fraction of player's base damage the shade deals (0–1). */
  readonly shadeDamageMultiplier: number;
  /** Shade movement speed (integration layer applies px/s). */
  readonly shadeSpeed: number;
  /** How much enemy aggro the shade draws (0–1). */
  readonly shadeAggroWeight: number;
  /** Leash radius in tiles — shade won't wander further from player. */
  readonly shadeLeashRadiusTiles: number;
}

const DEFAULT_CONFIG: MirrorSkillConfig = {
  cooldownMs: 1200,            // medium cooldown tier
  shadeDamageMultiplier: 0.75, // spec: 75% of player's base damage
  shadeSpeed: 1.0,             // integration layer applies px/s
  shadeAggroWeight: 0.0,       // shade is a projection, draws no aggro
  shadeLeashRadiusTiles: 8,    // autonomous shade has leash range
};

// ---------------------------------------------------------------------------
// Resolver scope — uses 'tether' so inherited upgrades (Clarity, Synapse) apply
// ---------------------------------------------------------------------------

const RESOLVER_SCOPE = 'tether';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class MirrorSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'mirror';
  readonly failLabel = 'FIZZLE';

  private readonly deps: MirrorDeps;
  private readonly config: MirrorSkillConfig;

  private _shadeId = '';
  private _shadeAlive = false;
  private _fizzled = false;
  private _spawnsCount = 0;
  private _resolvedDamage = 0;
  private _lastSpawnX = 0;
  private _lastSpawnY = 0;

  constructor(deps: MirrorDeps, config: Partial<MirrorSkillConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get shadeAlive(): boolean { return this._shadeAlive; }
  get shadeId(): string { return this._shadeId; }
  get fizzled(): boolean { return this._fizzled; }
  get spawnsCount(): number { return this._spawnsCount; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get lastSpawnX(): number { return this._lastSpawnX; }
  get lastSpawnY(): number { return this._lastSpawnY; }

  // --- Integration layer hook ---

  /**
   * Called by the integration layer when the shade is destroyed externally
   * (e.g., player death). Clears shade state and deactivates without
   * starting cooldown — the player is dead, cooldown is irrelevant.
   */
  onShadeDestroyed(): void {
    this._shadeAlive = false;
    this._shadeId = '';
    this._isActive = false;
  }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this.cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();
    const baseDamage = this.deps.player.getBaseDamage();

    // Resolve shade damage multiplier via tether scope — max aggregation
    // picks the highest registered value (Mirror's 0.75 > Clarity's 0.55)
    const mult = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'shadeDamageMultiplier')
      ?? this.config.shadeDamageMultiplier;
    this._resolvedDamage = baseDamage * mult;

    // Autonomous shade spawns at player position — it will navigate to
    // its own target from there via the 'autonomous' AI mode
    this._lastSpawnX = pos.x;
    this._lastSpawnY = pos.y;

    const shadeConfig: ShadeSummonConfig = {
      id: 'mirror_shade',
      archetype: 'shade',
      baseHp: 9999,  // shade has no independent HP; damage routes to player
      baseDamage: this._resolvedDamage,
      baseSpeed: this.config.shadeSpeed,
      leashRadiusTiles: this.config.shadeLeashRadiusTiles,
      aggroWeight: this.config.shadeAggroWeight,
      aiMode: 'autonomous',
      shadeDamageMultiplier: mult,
      mirrorDistanceTiles: 0,  // not used in autonomous mode
      sharedHpPool: true,
    };

    this._shadeId = this.deps.summons.spawn(shadeConfig, pos.x, pos.y);
    this._shadeAlive = true;
    this._isActive = true;
    this._spawnsCount++;

    return true;
  }

  protected onDeactivate(): void {
    if (this._shadeAlive) {
      this.deps.summons.destroy(this._shadeId);
      this._shadeAlive = false;
      this._shadeId = '';
    }
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(_delta: number): void {
    // Autonomous shade movement and target selection is handled by the
    // integration layer via the 'autonomous' AI mode. The SkillEffect
    // manages only the spawn/dismiss lifecycle.
  }

  destroy(): void {
    if (this._shadeAlive) {
      this.deps.summons.destroy(this._shadeId);
      this._shadeAlive = false;
      this._shadeId = '';
    }
    super.destroy();
  }
}
